import { usePageLayout, useCart, NavigationListTypes } from '@libs/common-next';
import { Country, Currency, DeliveryType, getCountry } from '@libs/core';
import { useAddToCTCartDrawer, useProductDetails } from '@app/pdp/hooks';
import {
    getSearchStats,
    trackEEAddToCart,
    TrackingParams,
    velocityTrackAddToCart,
} from '@libs/analytics';
import type { ProductHit } from '@libs/algolia';
import type { FulfillmentGroupsItemUI } from '@libs/common-node';
import { mapFBTAddToCartItems } from '@app/pdp/utils';
import { AddToCartType } from '@libs/ocapi';

type FBTProduct = ProductHit & { isSelected: boolean };

interface GetNormalizedProductsProps {
    products: FBTProduct[];
    country: Country;
    storeId: string;
}

/**
 * Transforms a list of products into a normalized format.
 */
const getNormalizedProducts = ({
    products,
    country,
    storeId,
}: Readonly<GetNormalizedProductsProps>) => {
    const DEFAULT_QUANTITY = 1;

    const getProductSku = (product: FBTProduct) =>
        product?.sku ? String(product.sku) : String(product.id);

    return products.map(product => ({
        storeId,
        country,
        sku: getProductSku(product),
        quantity: DEFAULT_QUANTITY,
        fulfillmentMethod: DeliveryType.BOPIS, // BOPIS is by default
    }));
};

/**
 * A custom hook that provides error handling functionality.
 *
 * The `useHandleError` hook is designed to manage error-related state and behavior within a component.
 * It includes a method to log error messages to the console and trigger an error alert in the UI.
 */
const useHandleError = () => {
    const { setErrorAlert } = usePageLayout();

    const handleError = (errorMessage?: string) => {
        console.error(errorMessage);
        setErrorAlert(true);
    };

    return { handleError };
};

interface HandleAddToCTCartProps {
    storeId: string;
    products: FBTProduct[];
    onStartCallback?: () => void;
    onEndCallback?: () => void;
}

export const useAddFBTToCTCart = () => {
    const country = getCountry();
    const { addItem, cart, itemsList } = useCart();
    const { handleError } = useHandleError();
    const { openAddedToCartDrawer } = useAddToCTCartDrawer();
    const { product, selectedDeliveryMethod } = useProductDetails();
    const {
        selectedStore: { store },
    } = usePageLayout();
    const currency = country === Country.US ? Currency.USD : Currency.CAD;

    const handleAddToCTCart = async ({
        products,
        storeId,
        onStartCallback,
        onEndCallback,
    }: Readonly<HandleAddToCTCartProps>) => {
        // Start
        onStartCallback?.();

        // 1) Preparing products for drawer
        let fulfillmentGroups: FulfillmentGroupsItemUI[] = [];

        // 2) Normalize products
        const normalizedProducts = getNormalizedProducts({
            products,
            country,
            storeId,
        });

        const { searchTerm } = getSearchStats();
        velocityTrackAddToCart(
            product,
            currency,
            NavigationListTypes.STANDARD_PDP,
            'yes',
            mapFBTAddToCartItems(
                product,
                products,
                store,
                itemsList.length + 1,
                selectedDeliveryMethod,
                searchTerm
            )
        );

        // 3) Execute promises sequentially - `for(...)` is the best way
        let newCart = cart;
        for (let index = 0; index < normalizedProducts.length; index += 1) {
            const product = normalizedProducts[index];

            try {
                newCart = await addItem(product, newCart || undefined);
                /**
                 * Here we can check for the last element of the array,
                 * and do this operation in the last iteration,
                 *
                 * if (newCart && isLastProduct) {...}
                 *
                 * but we cannot do this because the second product may be added to the cart
                 * and the third may not be added,
                 * then we will not show the drawer,
                 * although the products have been added
                 * so need to update `fulfillmentGroups` on each iteration
                 */
                if (newCart) {
                    trackEEAddToCart(
                        [
                            {
                                algoliaHit: products[index],
                                parameters: {
                                    quantity: product.quantity,
                                    fulfillmentMethod:
                                        product.fulfillmentMethod,
                                    type: TrackingParams.STANDARD_PDP,
                                    storeID: product.storeId,
                                },
                            },
                        ],
                        AddToCartType.RECS
                    );
                    fulfillmentGroups = newCart.fulfillmentGroups;
                } else {
                    handleError('No cart id returned.');
                }
            } catch (error) {
                handleError(
                    `An error happened when trying to add to cart. ${
                        (error as Error).message
                    }`
                );
            }
        }

        // 4) If new products have been added to the array
        if (fulfillmentGroups.length) {
            // Sku list of new added products
            const productsSkus = new Set(
                normalizedProducts.map(product => product.sku)
            );

            // Find added products in the new cart
            const productsForDrawer = fulfillmentGroups
                .find(
                    group =>
                        // The `.toLowerCase()` method is needed to make sure that the compared strings are equals
                        group.fulfillmentMethod.toLowerCase() ===
                        DeliveryType.BOPIS.toLowerCase()
                )
                ?.items.filter(product => productsSkus.has(product.sku));

            // Show drawer if there are products
            if (productsForDrawer?.length) {
                openAddedToCartDrawer([...productsForDrawer]);
            }
        }

        // End
        onEndCallback?.();
    };

    return { handleAddToCTCart };
};
